import * as React from "react";
import { lerp } from "./animations";
import { LERP_FACTOR } from "./constants";

export interface UseCursorFollowOptions {
  lerpFactor?: number;
  elementRef: React.RefObject<HTMLElement>;
  onHoverClass?: string;
}

export const useCursorFollow = ({
  lerpFactor = LERP_FACTOR.cursor,
  elementRef,
  onHoverClass,
}: UseCursorFollowOptions) => {
  const targetPos = React.useRef({ x: 0, y: 0 });
  const currentPos = React.useRef({ x: 0, y: 0 });
  const animationRef = React.useRef<number | null>(null);
  const isHovering = React.useRef(false);

  const animate = React.useCallback(() => {
    if (!elementRef.current) return;

    currentPos.current.x = lerp(
      currentPos.current.x,
      targetPos.current.x,
      lerpFactor,
    );
    currentPos.current.y = lerp(
      currentPos.current.y,
      targetPos.current.y,
      lerpFactor,
    );

    elementRef.current.style.setProperty(
      "--cursor-x",
      `${currentPos.current.x}px`,
    );
    elementRef.current.style.setProperty(
      "--cursor-y",
      `${currentPos.current.y}px`,
    );

    if (isHovering.current) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [elementRef, lerpFactor]);

  const handleMouseMove = React.useCallback(
    (e: React.MouseEvent) => {
      if (!elementRef.current) return;
      const rect = elementRef.current.getBoundingClientRect();
      targetPos.current.x = e.clientX - rect.left;
      targetPos.current.y = e.clientY - rect.top;
    },
    [elementRef],
  );

  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent) => {
      if (!elementRef.current) return;
      isHovering.current = true;
      if (onHoverClass) {
        elementRef.current.classList.add(onHoverClass);
      }

      const rect = elementRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      targetPos.current = { x, y };
      currentPos.current = { x, y };

      animationRef.current = requestAnimationFrame(animate);
    },
    [elementRef, onHoverClass, animate],
  );

  const handleMouseLeave = React.useCallback(() => {
    isHovering.current = false;
    if (elementRef.current && onHoverClass) {
      elementRef.current.classList.remove(onHoverClass);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, [elementRef, onHoverClass]);

  React.useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
  };
};
